<script lang="ts">
    import dayjs from 'dayjs'
    import Content from '../../_common/content.svelte'
    import { DragAndDrop } from '../../pipeline/dragAndDrop/dragAndDrop.js'
    import { onMount, tick } from 'svelte'
    import Zact from '../../_common/z/zact.svelte'
    import DateSelect from '../../_common/date-select.svelte'
    import { CategoryTypeEnum, CategoryTypeFriendlyEnum } from '../../../modules/financialApi/categories/category.enum'
    import Zbutton from '../../_common/z/zbutton.svelte'
    import { faEye, faPlusCircle, faArrowsAlt, faRemove, faSave, faEdit } from '@fortawesome/free-solid-svg-icons'
    import Fa from 'svelte-fa'
    import Zinput from '../../_common/z/zinput.svelte'
    import DateSelectMonth from '../../_common/date-select-month.svelte'
    import type { ReportEntity } from '../../../modules/financialApi/reports/report'
    import type { Category } from '../../../modules/financialApi/categories/category'
    import { GridBadge, GridBadgeClassEnum, GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../../_common/z/zgrid'
    import Zgrid from '../../_common/z/zgrid.svelte'
    import Popover from '../../_common/popover.svelte'
    import Financial_4RelatResultDetail from '../result/financial-4-relat-result-detail.svelte'
    import type { Entry } from '../../../modules/financialApi/entries/entry'

    let edit = false

    // regime de competência é opcional: quando a empresa não usa, o gerador fica travado em caixa
    // (a mesma configuração já esconde da lista os relatórios por competência — ver loadReports)
    const showAccrual = window.configs.financialShowAccrual

    let elements: { source: 'title' | 'tot' | 'sub' | 'par' | 'esp'; label: string; enabled: boolean }[] = [
        { source: 'title', label: 'Título', enabled: true },
        { source: 'sub', label: 'Subtotal', enabled: true },
        { source: 'tot', label: 'Total', enabled: true },
        { source: 'esp', label: 'Espaçamento', enabled: true },
    ]

    let categories: Category[] = []
    let categoriesPos: Category[] = []

    async function loadCategories() {
        categoriesPos = []
        categories = await window.api.financials.categories.getAll()
        categories.sort((a, b) => a.label.localeCompare(b.label))
        categories.sort((a, b) => a.label.localeCompare(b.type))
        const sell = categories.find((c) => c.label === 'Vendas - venda realizada no PDV')
        const tax = categories.find((c) => c.label === 'Banco - taxas bancárias, de maquinha de cartão e meios de pagamento')
        if (sell) categoriesPos.push(sell)
        if (tax) categoriesPos.push(tax)
    }

    async function loadData(type: 'accrual' | 'cash') {
        let entries: { categoryId: string; value: number; items: [] }[] = []
        let entriesPos: { categoryId: string; value: number; items: [] }[] = []
        if (type === 'accrual') {
            entries = await window.api.financials.entries.getEntriesByCategoryAndAccrual(dates.from, dates.to)
            entriesPos = await window.api.financials.entries.getEntriesByCategoryAndAccrual(dates.from, dates.to, true)
        } else {
            entries = await window.api.financials.entries.getEntriesByCategoryAndCash(dates.from, dates.to)
            entriesPos = await window.api.financials.entries.getEntriesByCategoryAndCash(dates.from, dates.to, true)
        }

        data = []
        for (const category of categoriesPos) {
            let item: iData = data.find((d) => d.type === category.type && d.source === 'pdv')
            if (!item) {
                item = {
                    source: 'pdv',
                    type: category.type,
                    label: CategoryTypeFriendlyEnum[category.type],
                    value: 0,
                    enabled: true,
                    categories: [],
                }
                data.push(item)
            }
            let cat = {
                id: category.categoryId,
                label: category.label,
                value: 0,
                enabled: true,
                status: category.status,
                entries: [],
            }
            const entry = entriesPos.find((e) => e.categoryId === category.categoryId)
            if (entry) {
                if (category.type.toString().startsWith('credito_')) {
                    cat.value = entry.value
                } else {
                    cat.value = -entry.value
                }
                cat.entries = entry.items
                item.value += cat.value
            }

            item.categories.push(cat)
        }

        for (const category of categories) {
            let item: iData = data.find((d) => d.type === category.type && d.source === 'mov')
            if (!item) {
                item = {
                    source: 'mov',
                    type: category.type,
                    label: CategoryTypeFriendlyEnum[category.type],
                    value: 0,
                    enabled: true,
                    categories: [],
                }
                data.push(item)
            }
            let cat = {
                id: category.categoryId,
                label: category.label,
                value: 0,
                enabled: true,
                status: category.status,
                entries: [],
            }
            const entry = entries.find((e) => e.categoryId === category.categoryId)
            if (entry) {
                if (category.type.toString().startsWith('credito_')) {
                    cat.value = entry.value
                } else {
                    cat.value = -entry.value
                }
                cat.entries = entry.items
                item.value += cat.value
            }

            item.categories.push(cat)
        }
        data = data
    }

    let report: ReportEntity = null

    let reports: ReportEntity[] = []
    async function loadReports() {
        reports = await window.api.financials.reports.getAll()
        if (!window.configs.financialShowAccrual) {
            reports = reports.filter((r) => r.data.type !== 'accrual')
        }
    }

    interface iData {
        source: 'pdv' | 'mov'
        type: CategoryTypeEnum
        label: string
        value: number
        enabled: boolean
        categories: {
            id: string
            label: string
            value: number
            enabled: boolean
            status: number
            entries: Entry[]
        }[]
    }
    let data: iData[] = []

    let result: { source: string; label: string; value?: number; id: string; categoryId: string }[] = []

    function generateResult() {
        for (const element of elements) {
            element.enabled = true
        }
        for (const item of data) {
            item.enabled = true
            for (const cat of item.categories) {
                cat.enabled = true
            }
        }
        result = []
        let subtotal = 0
        let total = 0
        for (let row of report?.data?.rows) {
            if (row.source === 'title') {
                result.push({ source: 'title', label: row.label, id: crypto.randomUUID(), categoryId: null })
                continue
            }
            if (row.source === 'sub') {
                result.push({ source: 'sub', label: row.label, value: subtotal, id: crypto.randomUUID(), categoryId: null })
                subtotal = 0
                continue
            }
            if (row.source === 'esp') {
                result.push({ source: 'esp', label: '', value: null, id: crypto.randomUUID(), categoryId: null })
                subtotal = 0
                continue
            }
            if (row.source === 'tot') {
                result.push({ source: 'tot', label: row.label, value: total, id: crypto.randomUUID(), categoryId: null })
                // let dataRow = elements.find((e) => e.source === 'tot')
                // if (dataRow) {
                //     dataRow.enabled = false
                //     elements = elements
                // }
                continue
            }
            if (row.source === 'pdv' || row.source === 'mov') {
                let dataRow
                if (row.id.length === 36 && row.id.includes('-')) {
                    dataRow = data.find((c) => c.source === row.source && c.categories.some((c) => c.id === row.id))?.categories.find((c) => c.id === row.id)
                } else {
                    dataRow = data.find((c) => c.source === row.source && c.type === row.id)
                }
                if (dataRow) {
                    result.push({ source: row.source, label: dataRow.label, value: dataRow.value, id: crypto.randomUUID(), categoryId: row.id })
                    subtotal += dataRow.value
                    total += dataRow.value
                    dataRow.enabled = false
                    data = data
                }
            }
        }
        result = result
        elements = elements
        data = data
        if (edit) attachDragAndDrop()
        loadGrid()
    }

    let grid: GridClass = new GridClass()
    function loadGrid() {
        // grid
        grid = new GridClass()
        // grid head
        grid.title = report.data.title
        grid.showHeaders = true
        grid.showFilter = false
        grid.showLines = true
        grid.showActs = true
        grid.defaultEmptyValue = ''
        grid.subtitle = generateSubtitle(report.data.type)
        //grid.cols.push(new GridCol({ content: 'Origem', type: GridColTypeEnum.badge, class: 'max-width-badge' }))
        grid.cols.push(new GridCol({ content: 'Descrição' }))
        grid.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.float_2 }))
        grid.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        // grid lines
        for (const item of result) {
            let lineClass = ''
            if (item.source === 'esp') {
                continue
                lineClass += 'fin-rel-2_space'
            } else if (item.source === 'title') {
                lineClass += 'fin-rel-2_title'
            } else if (item.source === 'sub') {
                lineClass += 'fin-rel-2_subtotal'
            } else if (item.source === 'tot') {
                lineClass += 'fin-rel-2_total'
            }

            let btns = []
            if (item.source === 'pdv' || item.source === 'mov') {
                let btnDetails = new GridButton({
                    callback: () => goShowDetails(item.categoryId, item.source as any),
                    icon: faEye,
                })
                btns.push(btnDetails)
            }
            let line: GridLine = new GridLine()
            let badge: GridBadge = null
            if (item.source === 'pdv') {
                badge = new GridBadge({ label: item.source, class: GridBadgeClassEnum.badge_success })
            } else if (item.source === 'mov') {
                badge = new GridBadge({ label: item.source, class: GridBadgeClassEnum.badge_purple })
            }
            //line.slots.push({ content: badge })
            line.slots.push({ content: item.label, class: lineClass, badge: badge })
            line.slots.push({ content: item.value, class: lineClass })
            line.slots.push({ content: btns, class: lineClass })
            grid.lines.push(line)
        }
    }

    let categoryId: string = null
    let detailsEntries: Entry[] = []
    async function goShowDetails(_categoryId: string, source: 'pdv' | 'mov') {
        detailsEntries = []
        for (const item of data) {
            if (item.source !== source) continue
            for (const cat of item.categories) {
                if (cat.id === _categoryId || item.type === _categoryId) {
                    detailsEntries = detailsEntries.concat(cat.entries)
                }
            }
        }
        detailsEntries = await window.api.utils.utils.assignAccountFromObject(detailsEntries)
        detailsEntries = await window.api.utils.utils.assignFinancialCategoryFromObject(detailsEntries)
        detailsEntries = detailsEntries
        categoryId = _categoryId
    }

    onMount(async () => {
        await loadReports()
        await loadCategories()
    })

    function generateSubtitle(type: 'accrual' | 'cash') {
        let label = 'Regime de '
        label += type === 'accrual' ? 'competência' : 'caixa'
        label += ' de '
        label += dayjs(dates.from).format('DD/MM/YYYY')
        label += ' até '
        label += dayjs(dates.to).format('DD/MM/YYYY')
        return label
    }

    let dates = window.api.utils.dates.currentMonth()

    async function goSelect(r: ReportEntity) {
        report = r
        await loadData(report.data.type)
        generateResult()
        console.log('report', r)
    }

    async function dateChanged(event) {
        dates = event.detail
        await goSelect(report)
    }

    async function reportTypeOnChange(event) {
        await goSelect(report)
    }

    function addItem(source, label, id) {
        report?.data?.rows.push({ source, label, id })
        generateResult()
        //
    }

    function editTitle(i: number) {
        const result = prompt('Informe o novo título', report?.data?.rows[i].label)
        if (!result) return
        report.data.rows[i].label = result
        generateResult()
    }

    function editSubtitle(i: number) {
        const result = prompt('Informe o novo subtítulo', report?.data?.rows[i].label)
        if (!result) return
        report.data.rows[i].label = result
        generateResult()
    }

    let dd: DragAndDrop = null
    function attachDragAndDrop() {
        dd = new DragAndDrop('fin-rel_content_table')
        dd.callback = (result) => listReordered(result)
    }

    async function listReordered(result) {
        let sourceIndex = result.source.index
        let destinationIndex = result.destination.index
        const element = report?.data?.rows.splice(sourceIndex, 1)[0]
        // Insere o elemento no novo índice
        report?.data?.rows.splice(destinationIndex, 0, element)
        await tick()
        generateResult()
    }

    $: {
        if (dd) {
            dd.enabled = edit
        }
    }

    async function goEdit() {
        edit = true
        await goSelect(report)
    }

    async function removeReport() {
        if (!confirm('Deseja mesmo excluir este relatório?')) {
            return
        }
        try {
            await window.api.financials.reports.remove(report.reportId)
            report = null
            edit = false
            await loadReports()
        } catch (error) {
            window.error(error)
        } finally {
        }
    }

    async function createReport() {
        const dto: Partial<ReportEntity> = {
            data: {
                title: 'Novo relatório',
                type: showAccrual ? 'accrual' : 'cash',
                rows: [],
            },
        }
        report = await window.api.financials.reports.create(dto)
        await loadReports()
        await goEdit()
    }

    async function updateReport(close: boolean = true) {
        const dto: Partial<ReportEntity> = {
            data: report.data,
        }
        await window.api.financials.reports.update(report.reportId, dto)
        await loadReports()
        if (close) {
            edit = false
        }
    }

    async function updateTitle() {
        const result = prompt('Informe o novo título', report?.data?.title)
        if (!result) return
        report.data.title = result
    }

    function removeReportRow(i: number) {
        report.data.rows.splice(i, 1)
        generateResult()
    }

    const reportTypes = [
        { value: 'accrual', label: 'Competência', disabled: !showAccrual },
        { value: 'cash', label: 'Caixa' },
    ]

    function formChanged() {
        goSelect(report)
    }
</script>

{#if categoryId}
    <Popover title="Detalhamento" width="1250px" height="100px" on:close={() => (categoryId = null)}>
        <Financial_4RelatResultDetail entries={detailsEntries} on:change={formChanged}></Financial_4RelatResultDetail>
    </Popover>
{/if}
<Content footer={true}>
    <div class="config-box h-space">
        <div class="fin-rel">
            {#if !edit}
                <div class="fin-rel_list h-print">
                    <div class="fin-rel_list_header">
                        <div class="fin-rel_list_title">Relatórios</div>
                        <div class="fin-rel_list_act">
                            <Zbutton
                                icon={faPlusCircle}
                                on:click={() => {
                                    createReport()
                                }}
                            />
                        </div>
                    </div>
                    <div class="fin-rel_list_body">
                        {#each reports as report}
                            <div on:click={() => goSelect(report)} class="fin-rel_list_content">{report.data?.title}</div>
                        {/each}
                    </div>
                </div>
            {/if}
            {#if edit && report}
                <div class="fin-rel_options scroll-dark">
                    <!-- <div class="fin-rel_options_header">
                        <div class="fin-rel_options_act">
                            <Zbutton
                                label="Excluir"
                                color="red"
                                icon={faRemove}
                                on:click={() => {
                                    removeReport()
                                }}
                            />
                            <div class="spacer"></div>
                            <Zbutton
                                label="Salvar"
                                icon={faSave}
                                on:click={() => {
                                    updateReport()
                                }}
                            />
                        </div>
                    </div> -->
                    <div class="fin-rel_options_title mt-2">Configurações</div>
                    <Zinput type="select" label="Regime" bind:value={report.data.type} on:change={reportTypeOnChange} options={reportTypes} />
                    <div class="fin-rel_options_title mt-2">Dados Disponíveis</div>
                    <div class="fin-rel_options_content fin-rel_options_content_elements">
                        {#each elements as element}
                            <div class="fin-rel_options_content_item">
                                <div
                                    class="fin-rel_options_content_item_header -bold fin-rel_options_content_item_header_disabled-{!element.enabled}"
                                    on:click={() => {
                                        if (element.enabled) {
                                            addItem(element.source, element.label, null)
                                        }
                                    }}
                                >
                                    <div class="fin-rel_options_content_item_badge">
                                        <div class="zbadge_default">{element.source}</div>
                                    </div>
                                    <div class="fin-rel_options_content_item_label">{element.label}</div>
                                </div>
                            </div>
                        {/each}
                        {#each data as item}
                            <div class="fin-rel_options_content_item">
                                <div
                                    class="fin-rel_options_content_item_header -bold fin-rel_options_content_item_header_disabled-{!item.enabled}"
                                    on:click={() => {
                                        if (item.enabled) {
                                            addItem(item.source, item.label, item.type)
                                        }
                                    }}
                                >
                                    <div class="fin-rel_options_content_item_badge">
                                        <div class="zbadge_default zbadge_{item.source === 'pdv' ? 'success' : 'purple'}">{item.source}</div>
                                    </div>
                                    <div class="fin-rel_options_content_item_label">{item.label}</div>
                                </div>
                                <div class="fin-rel_options_content_item_body">
                                    {#each item.categories as cat}
                                        {#if cat.status >= 100}
                                            <div
                                                class="fin-rel_options_content_item_body_line fin-rel_options_content_item_body_line_disabled-{!cat.enabled ||
                                                    !item.enabled}"
                                                on:click={() => {
                                                    if (cat.enabled) {
                                                        addItem(item.source, cat.label, cat.id)
                                                    }
                                                }}
                                            >
                                                <div class="fin-rel_options_content_item_badge">
                                                    <div class="zbadge_default zbadge_{item.source === 'pdv' ? 'success' : 'purple'}">{item.source}</div>
                                                </div>
                                                <div class="fin-rel_options_content_item_label">{cat.label}</div>
                                            </div>
                                        {/if}
                                    {/each}
                                </div>
                            </div>
                        {/each}
                    </div>
                </div>
            {/if}

            <div class="fin-rel_content fin-rel_content-edit-{edit}">
                {#if !report}
                    <div class="text-muted">Nenhum relatório selecionado</div>
                {:else if !edit}
                    <div class="fin-rel_content_header h-print">
                        <div class="fin-rel_content_header_act h-print">
                            {#if report.data.type === 'accrual'}
                                <DateSelectMonth startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
                            {:else}
                                <DateSelect startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
                            {/if}
                        </div>
                        <div class="fin-rel_content_header_content"></div>
                        <div class="fin-rel_content_header_act h-print">
                            {#if !report?.system}
                                <Zbutton
                                    icon={faEdit}
                                    on:click={() => {
                                        goEdit()
                                    }}
                                />
                            {/if}
                        </div>
                    </div>

                    <Zgrid {grid}></Zgrid>
                {:else}
                    <div class="fin-rel_content_header">
                        <div class="fin-rel_content_header_content">
                            <div class="fin-rel_content_header_title">
                                <span>{report.data.title}</span>
                                {#if edit}
                                    <span class="fin-rel_content_header_title-edit" on:click={() => updateTitle()}><Fa icon={faEdit}></Fa></span>
                                {/if}
                            </div>
                        </div>
                        <div class="fin-rel_content_header_act h-print">
                            <Zbutton
                                color="red"
                                icon={faRemove}
                                on:click={() => {
                                    removeReport()
                                }}
                            />
                            <Zbutton
                                icon={faSave}
                                on:click={() => {
                                    updateReport()
                                }}
                            />
                        </div>
                    </div>

                    <div class="fin-rel_content_table" id="fin-rel_content_table">
                        {#if edit && result.length === 0}
                            <div class="text-muted fin-rel_content_table_helper">Clique nos elementos ao lado para montar o relatório</div>
                        {/if}
                        {#each result as row, i (row.id)}
                            <div class="fin-rel_content_table_row">
                                <div
                                    class="fin-rel_content_table_row_remove"
                                    on:click={() => {
                                        removeReportRow(i)
                                    }}
                                >
                                    <Fa icon={faRemove} />
                                </div>
                                <div class="fin-rel_content_table_row_drag">
                                    <Fa icon={faArrowsAlt} />
                                </div>
                                {#if row.source === 'title'}
                                    <div class="fin-rel_content_table_row_title">
                                        <span>{row.label}</span>
                                        {#if edit}
                                            <span class="fin-rel_content_table_row_title-edit" on:click={() => editTitle(i)}><Fa icon={faEdit}></Fa></span>
                                        {/if}
                                    </div>
                                {:else if row.source === 'sub'}
                                    <div class="fin-rel_content_table_row_subtotal_label">
                                        <span>{row.label}</span>
                                        {#if edit}
                                            <span class="fin-rel_content_table_row_title-edit" on:click={() => editTitle(i)}><Fa icon={faEdit}></Fa></span>
                                        {/if}
                                    </div>
                                    <div class="fin-rel_content_table_row_subtotal_value zgrid_caption_td_content">R$ {row.value.toFixed(2)}</div>
                                {:else if row.source === 'tot'}
                                    <div class="fin-rel_content_table_row_total_label">
                                        <span> {row.label} </span>
                                        {#if edit}
                                            <span class="fin-rel_content_table_row_title-edit" on:click={() => editTitle(i)}><Fa icon={faEdit}></Fa></span>
                                        {/if}
                                    </div>
                                    <div class="fin-rel_content_table_row_total_value zgrid_caption_td_content">R$ {row.value.toFixed(2)}</div>
                                {:else if row.source === 'esp'}
                                    <div class="fin-rel_content_table_row_space">&nbsp;</div>
                                {:else}
                                    <div class="fin-rel_content_table_row_source">
                                        <div class="zbadge_default zbadge_{row.source === 'pdv' ? 'success' : 'purple'}">{row.source}</div>
                                    </div>
                                    <div class="fin-rel_content_table_row_label">{row.label}</div>
                                    <div class="fin-rel_content_table_row_value">R$ {row.value.toFixed(2)}</div>
                                {/if}
                            </div>
                        {/each}
                    </div>
                {/if}
            </div>
        </div>
    </div>
</Content>

<style>
    .max-width-badge {
        max-width: 45px;
    }
</style>
